import { api } from '@/lib/api-client'
import { isDemoIssue, isDemoSite } from '@/shared/constants/demo-data'
import type {
  IssueDerivedStatus,
  IssueSeverity,
  IssueStatus,
} from '@/shared/constants/issue-status'

// ── HOD inbox bucket model (lifecycle v2) ──────────────────────────────────
// Backend exposes 4 DISJOINT buckets on GET /hod/issues:
//   open      → status='open'      (needs acknowledgement — HOD's todo)
//   pending   → status='pending' AND NOT overdue (acknowledged, in hand)
//   overdue   → status='pending' AND past resolution_eta (derived, never stored)
//   completed → status='completed' (terminal, includes late completions)
//
// 'pending' EXCLUDES overdue rows, so the four tab badges sum to the total.
// Every response includes bucket_counts for ALL 4 buckets — a single fetch
// populates the tab badges + the rendered list at once.

export type HodInboxBucket = 'open' | 'pending' | 'overdue' | 'completed'

export const HOD_INBOX_BUCKETS: ReadonlyArray<{
  value: HodInboxBucket
  label: string
}> = [
  { value: 'open', label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'completed', label: 'Completed' },
]

export interface HodInboxIssueRow {
  id: string
  issue_ref: string
  title: string
  description: string | null
  section_title: string | null
  severity: IssueSeverity
  /** Stored lifecycle value only (open | pending | completed). */
  status: IssueStatus
  /** Adds the time-derived overdue states — render badges from this. */
  derived_status: IssueDerivedStatus
  is_overdue: boolean
  /** ISO datetime the HOD committed to at acknowledge time. Null until then. */
  resolution_eta: string | null
  acknowledged_at: string | null
  site: { id: string; name: string }
  department: { id: string; name: string }
  /** Field Officer who originally raised the issue. Null when created_by
   *  is null (defensive — covers system-generated or migrated rows). */
  raised_by: { id: string; display_name: string } | null
  remark_count: number
  raised_at: string
  completed_at: string | null
  last_event_at: string | null
}

export interface HodBucketCounts {
  open: number
  pending: number
  overdue: number
  completed: number
}

export interface HodInboxResponse {
  bucket: HodInboxBucket
  bucket_counts: HodBucketCounts
  total_in_bucket: number
  page: number
  page_size: number
  issues: HodInboxIssueRow[]
}

export interface HodInboxParams {
  bucket: HodInboxBucket
  entity_id?: string
  site_id?: string
  severity?: IssueSeverity
  q?: string
  page?: number
  page_size?: number
}

export function listHodInbox(params: HodInboxParams): Promise<HodInboxResponse> {
  return api.get<HodInboxResponse>('/hod/issues', { params }).then((d) => {
    const issues = (d.issues ?? []).filter(
      (i) => !isDemoIssue(i.id) && !isDemoSite(i.site?.id),
    )
    const removed = (d.issues?.length ?? 0) - issues.length
    if (removed === 0) {return { ...d, issues }}
    // Decrement only the bucket this response is for; demo issues in other
    // buckets get netted out when those buckets are fetched. (No demo HOD
    // account exists, so this is belt-and-braces — the analytics views also
    // recompute from the filtered `issues` array.)
    return {
      ...d,
      issues,
      total_in_bucket: Math.max(0, d.total_in_bucket - removed),
      bucket_counts: {
        ...d.bucket_counts,
        [d.bucket]: Math.max(0, d.bucket_counts[d.bucket] - removed),
      },
    }
  })
}
